/**
 * Scroll spy of the sectioned dialogs (left step navigation / pill tabs): which section counts as "active".
 * Pure (no DOM): the DOM part lives in sectionScrollSpy.controller.ts. Check: tests/dialogs/sectionScrollSpy.check.ts
 *
 * Order of the rules:
 *  1. a section the user just jumped to via the navigation (locked until the scrolling has ended)
 *  2. the section containing the focused field, as long as it is (partly) in view
 *  3. scrolled to the bottom: the last section
 *  4. otherwise the last section whose top is at or above the anchor line near the top of the scroll container
 *     (not the most visible one, so a long section above does not "win" while the user works further down)
 */

/** A section in scroll content coordinates (0 = top of the scrollable content) */
export interface SpySection {
  id: string;
  top: number;
  bottom: number;
}

export interface SpyViewport {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

export interface SpyOptions {
  /** Section containing the focused field (if any) */
  focusedId?: string;
  /** Section clicked in the navigation, kept while the smooth scroll runs */
  lockedId?: string;
  /** Covered height at the top of the scroll container (e.g. sticky tabs), in px */
  topOffset?: number;
  /** Anchor line as a share of the visible height … */
  anchorRatio?: number;
  /** … clamped to these bounds (px below the visible top) */
  anchorMin?: number;
  anchorMax?: number;
  /** Distance to the end that still counts as "scrolled to the bottom" */
  bottomTolerance?: number;
}

export const SPY_DEFAULTS = {anchorRatio: 0.3, anchorMin: 48, anchorMax: 160, bottomTolerance: 4};

/** Distance of the anchor line below the visible top of the scroll container */
export function spyAnchorOffset(clientHeight: number, options: SpyOptions = {}): number {
  const ratio = options.anchorRatio ?? SPY_DEFAULTS.anchorRatio;
  const min = options.anchorMin ?? SPY_DEFAULTS.anchorMin;
  const max = options.anchorMax ?? SPY_DEFAULTS.anchorMax;
  return Math.min(Math.max(clientHeight * ratio, min), Math.max(min, max));
}

export function isScrolledToBottom(viewport: SpyViewport, tolerance = SPY_DEFAULTS.bottomTolerance): boolean {
  const scrollable = viewport.scrollHeight - viewport.clientHeight > tolerance;
  return scrollable && viewport.scrollTop > 0 && viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - tolerance;
}

/** Whether the section is at least partly inside the visible area */
export function isSectionInView(section: SpySection, viewport: SpyViewport, topOffset = 0): boolean {
  const visibleTop = viewport.scrollTop + topOffset;
  const visibleBottom = viewport.scrollTop + viewport.clientHeight;
  return section.bottom > visibleTop && section.top < visibleBottom;
}

/** Id of the active section, undefined without sections */
export function computeActiveSection(sections: SpySection[], viewport: SpyViewport, options: SpyOptions = {}): string | undefined {
  if (sections.length === 0) {
    return undefined;
  }
  const sorted = [...sections].sort((a, b) => a.top - b.top);
  const topOffset = options.topOffset || 0;
  if (options.lockedId && sorted.some((section) => section.id === options.lockedId)) {
    return options.lockedId;
  }
  const focused = options.focusedId ? sorted.find((section) => section.id === options.focusedId) : undefined;
  if (focused && isSectionInView(focused, viewport, topOffset)) {
    return focused.id;
  }
  if (isScrolledToBottom(viewport, options.bottomTolerance ?? SPY_DEFAULTS.bottomTolerance)) {
    return sorted[sorted.length - 1].id;
  }
  const anchor = viewport.scrollTop + topOffset + spyAnchorOffset(viewport.clientHeight - topOffset, options);
  let current = sorted[0];
  sorted.forEach((section) => {
    if (section.top <= anchor) {
      current = section;
    }
  });
  return current.id;
}
